import React, { useState, useEffect, useMemo } from 'react';
import { WhatsAppOutlined } from '@ant-design/icons';
import { Link, useHistory } from 'react-router-dom';
import { Alert, Row, Select, Space, Table, Tag, Typography, Popover, Popconfirm, Divider, message } from 'antd';
import _ from 'lodash';
import FeatherIcon from 'feather-icons-react';
import qs from 'qs';
import { useRouteMatch } from 'react-router-dom';
import moment from 'moment-timezone';
import useApiFetch from '../../../hooks/useApiFetch';
import { getItem } from '../../../utility/localStorageControl';
import { useAuth } from '../../../context/auth.context';
import { useNotification } from '../../../context/notification.context';
import { PageHeader } from '../../../components/page-headers/page-headers';
import { Button } from '../../../components/buttons/buttons';
import { BasicFormWrapper, Main, ProjectSorting, TableWrapper } from '../../styled';
import { Cards } from '../../../components/cards/frame/cards-frame';
import TableColumnManager from '../../../components/TableColumnManager';
import { DateAndTimeFormat } from '../../../config/config';
import SidePanel from '../../../components/SidePanel';
import { Form } from 'antd';
import { Input } from 'antd';
import { Upload } from 'antd';
import { ContactsPage } from '../contacts-style';

export default function ImportedContactLists() {
    const [noOfLists, setNoOfLists] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSizeOptions = [10, 30, 50, 80, 120];
    const { path } = useRouteMatch();
    const history = useHistory();
    const { apiClient } = useAuth();
    const [selectedStatus, setSelectedStatus] = useState(null);
    const { notifySuccess, notifyInfo, notifyWarning, notifyError } = useNotification();
    const [openImportModal, setOpenImportModal] = useState(false);
    const [importing, setImporting] = useState(false);
    const defaultedSelectedColumns = ['name', 'totalContacts', 'createdAt', 'emails', 'whatsApp', 'action'];
    const [selectedColumns, setSelectedColumns] = useState(
        getItem('imported-contacts-lists-columns') || defaultedSelectedColumns,
    );

    const [openColumnsManager, setOpenColumnsManager] = useState(false);
    const [notifyPlatform, setNotifyPlatform] = useState('none');
    const [form] = Form.useForm();

    const {
        data: lists,
        loading: listsLoading,
        refetch: refetchLists,
    } = useApiFetch('/api/contacts/import/lists', (data) => data.data, true);

    const { data: whatsappTemplates, loading: loadingWhatsappTemplates } = useApiFetch(
        `/api/broadcast-communications/whatsapp-templates?${qs.stringify({ status: 'ALL' })}`,
        (data) => data?.data?.filter((template) => template.status === 'APPROVED_AND_VALID') || [],
        true,
    );

    useEffect(() => {
        refetchLists(
            `/api/contacts/import/lists?${qs.stringify({
                take: pageSize,
                skip: (currentPage - 1) * pageSize,
                // status: selectedStatus,
            })}`,
        );
    }, [pageSize, currentPage, selectedStatus]);

    const handleDelete = async (id) => {
        try {
            const res = await apiClient.delete(`/api/contacts/import/lists?listId=${id}`);
            notifySuccess({
                message: res.data.message || 'Contact list deleted successfully',
            });
            await refetchLists();
        } catch (error) {
            console.error(error);
            notifyError({
                message: error.response?.data?.message || 'Error deleting communication',
            });
        }
        return false;
    };

    const dataSource = useMemo(
        () =>
            lists?.map((comm, index) => {
                const {
                    name,
                    totalCount,
                    createdAt,
                    description,
                    onBoardedContactsCount,
                    lastImportedAt,
                    createdBy,
                    lastModifiedBy,
                    totalEmailsSent,
                    totalEmailsOpen,
                    totalEmailsFailed,
                    totalWaMessagesSent,
                    totalWaMessagesRead,
                    totalWaMessagesFail,
                    uuid: id,
                } = comm;
                return {
                    key: index + 1,
                    totalContacts: totalCount,
                    emails: (
                        <Space direction="horizontal" align="baseline">
                            <FeatherIcon icon="mail" size={14} />
                            <span className="status info">
                                <strong>{totalEmailsSent || 0}</strong> Sent
                            </span>
                            {totalEmailsFailed > 0 && (
                                <span className="status error">
                                    <strong>{totalEmailsFailed || 0}</strong> Failed
                                </span>
                            )}
                            <span className="status success">
                                <strong>{totalEmailsOpen || 0}</strong> Opened
                            </span>
                        </Space>
                    ),
                    whatsApp: (
                        <Space direction="horizontal" align="baseline">
                            <WhatsAppOutlined />
                            <span className="status info">
                                <strong>{totalWaMessagesSent || 0}</strong> Sent
                            </span>
                            {totalWaMessagesFail > 0 && (
                                <span className="status error">
                                    <strong>{totalWaMessagesFail || 0}</strong> Failed
                                </span>
                            )}
                            <span className="status success">
                                <strong>{totalWaMessagesRead || 0}</strong> Read
                            </span>
                        </Space>
                    ),
                    name: (
                        <Typography.Link
                            href={`/admin/imported-contacts/${id}`}
                            style={{
                                maxWidth: '16rem',
                            }}
                            ellipsis
                        >
                            {name}
                        </Typography.Link>
                    ),
                    createdAt: moment(createdAt).format(DateAndTimeFormat),
                    action: (
                        <div className="table-actions" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                            <Button
                                size="small"
                                type="primary"
                                style={{
                                    height: '32px',
                                    borderRadius: '6px',
                                    fontWeight: 500,
                                    fontSize: '12px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    background: '#4F46E5',
                                    borderColor: '#4F46E5',
                                    color: '#FFFFFF',
                                    boxShadow: '0 1px 2px rgba(79, 70, 229, 0.2)',
                                }}
                                onClick={() => {
                                    const targetListId = id || comm.id || comm.uuid;
                                    history.push({
                                        pathname: '/admin/communications/add',
                                        search: targetListId ? `?listId=${targetListId}&isOnboarding=true` : '?isOnboarding=true',
                                        state: {
                                            selectedAll: true,
                                            selectedContactsCount: totalCount || 0,
                                            totalContacts: totalCount || 0,
                                            contactFilters: {
                                                listId: targetListId,
                                                listName: name,
                                                isImported: true,
                                                selectedContactsUuids: [],
                                            },
                                            isOnboarding: true,
                                        },
                                    });
                                }}
                            >
                                <FeatherIcon icon="send" size={13} />
                                <span>Onboarding</span>
                            </Button>
                            <Popconfirm
                                okText="Delete All Contacts & Stats"
                                okType="danger"
                                type="danger"
                                trigger={'click'}
                                onConfirm={() => handleDelete(id)}
                                title={
                                    <span>
                                        Are you sure you want to delete this list?
                                        <br />
                                        <strong>This will delete all contacts in this list and cannot be undone</strong>
                                        <br />
                                        <span>This action cannot be undone</span>
                                    </span>
                                }
                            >
                                <Button
                                    title={'Delete all contacts & stats'}
                                    className="btn-icon"
                                    type="danger"
                                    to="#"
                                    shape="circle"
                                >
                                    <FeatherIcon icon="trash" size={16} />
                                </Button>
                            </Popconfirm>
                        </div>
                    ),
                };
            }),
        [lists, history],
    );

    const downloadExampleCsv = async () => {
        try {
            const response = await fetch('/assets/contacts-import-example.csv');
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const contentType = response.headers.get('content-type') || '';
            if (!contentType.includes('csv') && !contentType.includes('text/plain') && !contentType.includes('octet-stream')) {
                throw new Error(`Unexpected content-type: ${contentType}`);
            }
            const blob = await response.blob();
            const objectUrl = window.URL.createObjectURL(blob);
            const downloadLink = document.createElement('a');
            downloadLink.href = objectUrl;
            downloadLink.download = 'contacts-import-example.csv';
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            window.URL.revokeObjectURL(objectUrl);
        } catch (error) {
            console.error(error);
            notifyError({ message: 'Could not download the example CSV file' });
        }
    };

    const handleOnImport = async (values) => {
        if (!values) {
            form.resetFields();
            return;
        }
        setImporting(true);
        try {
            const formData = new FormData();
            console.log(values);

            // Fix: Access the file correctly from Ant Design Upload component
            const file = values.file?.fileList?.[0]?.originFileObj || values.file;

            if (!file) {
                notifyError({
                    message: 'Please select a file to upload',
                });
                setImporting(false);
                return;
            }

            formData.append('files', file);
            const data = {
                name: values.name,
                description: values.description,
            };
            formData.append('data', JSON.stringify(data));

            const res = await apiClient.post(`/api/contacts/import`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            notifySuccess({
                message: res.data.message || 'Contacts imported successfully',
            });

            const listId = res.data?.data?.[0]?.listId;
            if (values.notifyPlatform && values.notifyPlatform !== 'none' && listId) {
                try {
                    const onBoardRes = await apiClient.post('/api/contacts/on-boarding', {
                        listId,
                        messagePlatform: values.notifyPlatform,
                        whatsAppTemplateId: values.whatsAppTemplateId,
                    });
                    notifySuccess({ message: onBoardRes.data.message });
                } catch (error) {
                    // The import itself succeeded, so this only downgrades to a warning — the contacts
                    // are in the list and can be on-boarded from the list page.
                    console.log(error);
                    notifyWarning({
                        message:
                            error.response?.data?.message ||
                            'Contacts imported, but the onboarding messages could not be queued.',
                    });
                }
            }

            form.resetFields();
            setNotifyPlatform('none');
            setOpenImportModal(false);
            refetchLists();
        } catch (error) {
            console.log(error);
            notifyWarning({
                message: error.response?.data?.message || 'Something went wrong while importing contacts',
            });
        }
        setImporting(false);
    };

    const allColumns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            align: 'left',
        },
        {
            title: 'Total Contacts',
            dataIndex: 'totalContacts',
            key: 'totalContacts',
        },
        {
            title: 'Emails',
            dataIndex: 'emails',
            key: 'emails',
        },

        {
            title: 'WhatsApp Reaches',
            dataIndex: 'whatsApp',
            key: 'whatsApp',
        },

        {
            title: 'Actions',
            dataIndex: 'action',
            key: 'action',
            align: 'right',
            fixed: 'right',
            width: 240,
        },
    ];

    return (
        <ContactsPage>
            <PageHeader
                routes={[
                    {
                        path: '/admin/overview',
                        icon: 'home',
                    },
                    {
                        breadcrumbName: 'Contacts',
                    },
                    {
                        breadcrumbName: 'Import Contacts',
                        path: window.location.pathname,
                    },
                ]}
                title="Import Contacts"
                buttons={[
                    <div key="1" className="page-header-actions">
                        <Button onClick={() => setOpenImportModal(true)} size="small" key="4" type="primary">
                            <FeatherIcon icon="plus" size={14} />
                            Create New List
                        </Button>
                        {/* <Link to="/admin/communications/add">
                        </Link> */}
                    </div>,
                ]}
            />
            <Main className="contacts-main">
                <Cards
                    headerActions={
                        <Space align="center">
                            {/* <ProjectSorting>
                                <div className="project-sort-bar">
                                    <div className="project-sort-nav">
                                        <nav>
                                            <ul>
                                                {communicationStatus.map((status) => (
                                                    <li
                                                        key={status}
                                                        className={selectedStatus === status ? 'active' : 'deactivate'}
                                                    >
                                                        <Link onClick={() => setSelectedStatus(status)} to="#">
                                                            {_.capitalize(status)}
                                                        </Link>
                                                    </li>
                                                ))}
                                            </ul>
                                        </nav>
                                    </div>
                                </div>
                            </ProjectSorting> */}

                            <Button type="primary" outlined loading={listsLoading} onClick={() => refetchLists()}>
                                <FeatherIcon icon="refresh-cw" size={14} />
                            </Button>
                            <Button type={'white'} onClick={() => setOpenColumnsManager(true)}>
                                <FeatherIcon icon="columns" size={16} /> Customize Columns
                            </Button>
                        </Space>
                    }
                >
                    <TableWrapper className="table-order table-responsive">
                        <Table
                            loading={listsLoading}
                            dataSource={dataSource}
                            columns={selectedColumns.map(
                                (column) => allColumns.find((col) => col.key === column) || {},
                            )}
                            pagination={{
                                pageSize: pageSize,
                                current: currentPage,
                                total: noOfLists,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} Communications`,
                                pageSizeOptions,
                                onShowSizeChange: (current, size) => {
                                    setPageSize(size);
                                },
                                showSizeChanger: true,
                                onChange: (page, pageSize) => {
                                    if (page !== currentPage) {
                                        setCurrentPage(page);
                                    }
                                },
                            }}
                        />
                    </TableWrapper>
                </Cards>
            </Main>
            <TableColumnManager
                saveKey={'imported-contacts-lists-columns'}
                onSave={(columns) => {
                    if (columns) {
                        setSelectedColumns(columns);
                    }
                    setOpenColumnsManager(false);
                }}
                open={openColumnsManager}
                title={'Manage Columns'}
                allColumns={allColumns}
                defaultSelected={defaultedSelectedColumns}
            />
            <SidePanel
                title="Create List"
                open={openImportModal}
                onCancel={() => {
                    setOpenImportModal(false);
                    form.resetFields();
                    setNotifyPlatform('none');
                }}
            >
                <BasicFormWrapper>
                    <Form onFinish={handleOnImport} form={form} initialValues={{ notifyPlatform: 'none' }}>
                        <Form.Item
                            rules={[
                                {
                                    required: true,
                                    message: 'please enter name for the list',
                                },
                            ]}
                            label="List Name"
                            name="name"
                        >
                            <Input maxLength={25} showCount />
                        </Form.Item>
                        <Form.Item
                            rules={[
                                {
                                    required: true,
                                    message: 'please enter description for the list',
                                },
                            ]}
                            label="Description"
                            name="description"
                        >
                            <Input.TextArea maxLength={160} showCount />
                        </Form.Item>
                        <Button
                            onClick={downloadExampleCsv}
                            icon={<FeatherIcon icon="download" size={16} />}
                            type="primary"
                            transparented
                            size="extra-small"
                        >
                            Download example CSV file format
                        </Button>
                        <br />
                        <br />
                        <Form.Item
                            label="Upload CSV"
                            name="file"
                            valuePropName="file"
                            rules={[
                                {
                                    required: true,
                                    message: 'please upload a CSV file',
                                },
                            ]}
                        >
                            <Upload
                                maxCount={1}
                                beforeUpload={(file) => {
                                    return false;
                                }}
                                type="drag"
                                name="file"
                                accept=".csv"
                            >
                                <FeatherIcon icon="upload" size={16} /> &nbsp;Drag and drop a CSV file here or click
                            </Upload>
                        </Form.Item>
                        <Form.Item
                            label="Notify contacts after import"
                            name="notifyPlatform"
                            extra="Sends a one-time notice telling these contacts that your communication has moved to CommKraft."
                        >
                            <Select
                                onChange={(value) => setNotifyPlatform(value)}
                                options={[
                                    { label: "Don't notify", value: 'none' },
                                    { label: 'Email', value: 'email' },
                                    { label: 'WhatsApp', value: 'whatsapp' },
                                    { label: 'Both', value: 'both' },
                                ]}
                            />
                        </Form.Item>
                        {(notifyPlatform === 'whatsapp' || notifyPlatform === 'both') && (
                            <>
                                {!loadingWhatsappTemplates && whatsappTemplates?.length === 0 && (
                                    <Alert
                                        message="No WhatsApp templates found"
                                        type="warning"
                                        showIcon
                                        style={{ marginBottom: '0.5rem' }}
                                        action={
                                            <Button
                                                href="/admin/communications/whatsapp-templates/add"
                                                icon={<FeatherIcon icon="external-link" size={14} />}
                                                size="extra-small"
                                                transparented
                                                type="primary"
                                            >
                                                Create Template
                                            </Button>
                                        }
                                    />
                                )}
                                <Form.Item
                                    name="whatsAppTemplateId"
                                    label="WhatsApp Template"
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Please select a WhatsApp template',
                                        },
                                    ]}
                                >
                                    <Select
                                        loading={loadingWhatsappTemplates}
                                        options={whatsappTemplates?.map((template) => ({
                                            label: template.name,
                                            value: template.uuid,
                                        }))}
                                    />
                                </Form.Item>
                            </>
                        )}
                        <div className="sDash_form-action">
                            <Button
                                size="default"
                                type={'light'}
                                onClick={() => {
                                    setOpenImportModal(false);
                                    form.resetFields();
                                    setNotifyPlatform('none');
                                }}
                            >
                                Cancel
                            </Button>
                            <Button loading={importing} size="default" type={'primary'} htmlType="submit">
                                Save
                            </Button>
                        </div>
                    </Form>
                </BasicFormWrapper>
            </SidePanel>
        </ContactsPage>
    );
}
